/*
 * Catalog PDP "AVAILABLE IN" (blocks/geisti-available-in.liquid): each medium
 * button moves the gallery carousel to that medium's photo, through Horizon's
 * own slideshow API (MediaGallery#slideshow.select) — no variant selection,
 * the catalog has no picker. The pressed button follows the slide shown,
 * including when the shopper swipes, via Horizon's `slideshow:select` event.
 * Reduced motion: the jump is instant (Horizon's select() already honours
 * prefers-reduced-motion; we also pass animate: false). When the gallery is
 * mostly off screen (phones: it sits above the buy box) a tap also scrolls it
 * into view, so the photo change is seen.
 *
 * A click also remembers the medium for this listing in sessionStorage
 * (`geisti:viewedMedium:<listingId>`); the shopper bundle reads it when it
 * links to their personalised product and appends ?variant= for that medium
 * (src/shopify-app/state/productLink.ts — keep the key in step).
 *
 * Loaded as a module by the block itself; test/scripts/available-in.test.ts
 * imports the exports from here.
 */

const GALLERY_ID = 'GeistiCatalogGallery';

const reducedMotion = () =>
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

// The index of the slide holding a media id, in Horizon's slide order.
export function slideIndexFor(slides, mediaId) {
  return slides.findIndex(slide => slide.querySelector(`[data-media-id="${mediaId}"]`));
}

// Mark the button whose media is on the given slide as pressed (none if the
// slide isn't any medium's photo).
export function syncPressed(root, slide) {
  const shown = slide?.querySelector('[data-media-id]')?.dataset.mediaId;
  for (const button of root.querySelectorAll('button[data-media-id]')) {
    button.setAttribute('aria-pressed', String(button.dataset.mediaId === shown));
  }
}

export const viewedMediumKey = listingId => `geisti:viewedMedium:${listingId}`;

function remember(root, medium, storage) {
  if (!root.dataset.listingId || !medium) return;
  try {
    storage.setItem(viewedMediumKey(root.dataset.listingId), medium);
  } catch {
    // Storage unavailable (private mode, quota): the choice just isn't carried.
  }
}

// On phones the gallery sits above the buy box, so a tap changes a photo the
// shopper can't see: bring the gallery into view when less than half of it
// is on screen (smooth; instant under reduced motion). On desktop it sits
// beside the row and is already visible, so nothing moves.
export function revealGallery(gallery) {
  const { top, bottom, height } = gallery.getBoundingClientRect();
  const visible = Math.min(bottom, window.innerHeight) - Math.max(top, 0);
  if (height > 0 && visible >= height / 2) return;
  gallery.scrollIntoView({ behavior: reducedMotion() ? 'instant' : 'smooth', block: 'start' });
}

export function initAvailableIn(root, gallery, storage = globalThis.sessionStorage) {
  if (!root || !gallery || root.dataset.geistiReady) return;
  root.dataset.geistiReady = '';
  if (!gallery.id) gallery.id = GALLERY_ID;
  const slideshow = () => gallery.slideshow;

  for (const button of root.querySelectorAll('button[data-media-id]')) {
    button.setAttribute('aria-controls', gallery.id);
    button.addEventListener('click', () => {
      const show = slideshow();
      const index = slideIndexFor(show?.slides ?? [], button.dataset.mediaId);
      if (index < 0) return;
      show.select(index, undefined, { animate: !reducedMotion() });
      syncPressed(root, show.slides[index]);
      revealGallery(gallery);
      remember(root, button.dataset.medium, storage);
    });
  }

  gallery.addEventListener('slideshow:select', event => syncPressed(root, event.detail?.slide));
  const show = slideshow();
  syncPressed(root, show?.slides?.[show.current ?? 0]);
}

if (typeof document !== 'undefined') {
  const root = document.querySelector('[data-geisti-available-in]');
  const gallery = root?.closest('.shopify-section')?.querySelector('media-gallery');
  if (gallery) customElements.whenDefined('media-gallery').then(() => initAvailableIn(root, gallery));
}
